import { Quad } from 'types';
import { searchWrapper } from './levenshteinDistance';
import { Core, WebViewerInstance } from '@pdftron/webviewer';

export interface SearchResult {
  start: number;
  end: number;
  content: string;
  distance: number;
}

const searchText = (text: string, search: string) => {
  const result = searchWrapper(search, text);
  return result;
};

const optimizeQuads = (instance: WebViewerInstance, quads: Quad[]): Core.Math.Quad[] => {
  if (quads.length === 0) return [];

  // We group differently depending if it's vertical or horizontal
  const isVerticalText = detectOrientation(quads);

  const groupMap = new Map();

  // we calculate the average dimension because if the text it's too big, rounding the decimals is not enough anymore to get a consitent line
  const averageDimension =
    quads.reduce((sum, quad) => {
      const dimension = isVerticalText
        ? Math.abs(quad.x3 - quad.x1) // Width for vertical text
        : Math.abs(quad.y3 - quad.y1); // Height for horizontal text
      return sum + dimension;
    }, 0) / quads.length;

  quads.forEach((quad) => {
    // we use the average dimension to get a key that would include all the quads that are close enough to be the same line
    const groupKey = isVerticalText
      ? `${Math.round(quad.x1 / averageDimension)}-${Math.round(quad.x3 / averageDimension)}`
      : `${Math.round(quad.y1 / averageDimension)}-${Math.round(quad.y4 / averageDimension)}`;

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }

    groupMap.get(groupKey).push(quad);
  });

  const mergedQuads: Core.Math.Quad[] = [];
  groupMap.forEach((groupQuads, _key) => {
    // Calculate the overall minimum and maximum values for the group so we have only one quad consolidating everything
    const minX = Math.min(...groupQuads.map((quad: Core.Math.Quad) => Math.min(quad.x1, quad.x2, quad.x3, quad.x4)));
    const maxX = Math.max(...groupQuads.map((quad: Core.Math.Quad) => Math.max(quad.x1, quad.x2, quad.x3, quad.x4)));
    const minY = Math.min(...groupQuads.map((quad: Core.Math.Quad) => Math.min(quad.y1, quad.y2, quad.y3, quad.y4)));
    const maxY = Math.max(...groupQuads.map((quad: Core.Math.Quad) => Math.max(quad.y1, quad.y2, quad.y3, quad.y4)));

    // Create the merged quad
    const mergedQuad = new instance.Core.Math.Quad(minX, minY, maxX, minY, maxX, maxY, minX, maxY);

    mergedQuads.push(mergedQuad);
  });
  return mergedQuads;
};

const detectOrientation = (quads: Quad[]): boolean => {
  let angleSum = 0;

  for (const quad of quads) {
    // Calculate the angle between the top-left and top-right points and transform to degrees
    let angle = Math.atan2(quad.y2 - quad.y1, quad.x2 - quad.x1) * (180 / Math.PI);

    // Normalize the angle to be within -90 to +90 degrees so we consider 180 to be horizontal
    if (angle > 90) {
      angle -= 180;
    } else if (angle < -90) {
      angle += 180;
    }

    angleSum += angle; // Keep raw signed angle for average calculation
  }

  const averageAngle = angleSum / quads.length;

  // If the average angle is closer to vertical (around 90 or -90 degrees), classify as vertical
  return Math.abs(averageAngle) > 45; // Adjust this threshold as needed
};

export { searchText, optimizeQuads, detectOrientation };
